import { getProductRouterPath } from '../../utils';
import { axiosProvider, unwrapResponseData } from '@/app/core/axios';
import {
  FullAttribute,
  AttributeLike,
  ProgramApiResponse,
  ProgramLifeCycleApiGatewayResponse,
  OutputLayoutField,
  FieldDelimiter,
  OutputLayoutFieldWithDetails,
  HasRowColor,
  ApiResponse,
  InputFileValidationApiGatewayResponse,
  InputLayoutField,
  ProgramDataType,
} from '@ssar-app/domain';
import sanitizeFilename from 'sanitize-filename';
import { saveAs } from 'file-saver';

export const getProgramById = async (
  programId: string,
): Promise<ProgramApiResponse> => {
  const response = await axiosProvider.apiClient.get<ProgramApiResponse>(
    `/program/${encodeURIComponent(
      getProductRouterPath(),
    )}?programId=${encodeURIComponent(programId)}`,
  );
  return unwrapResponseData(response);
};

export const getProgramSummaryForSubmitDownload = async (
  programName: string,
  model: ProgramDataType,
  version: string,
): Promise<void> => {
  const response = await axiosProvider.apiClient.post(
    `/program/${encodeURIComponent(getProductRouterPath())}/summary/download`,
    {
      model,
      version,
    },
  );
  if (response.data) {
    // decode base64 string, remove space for IE compatibility
    const binary = atob(response.data.replace(/\s/g, ''));
    const buffer = new ArrayBuffer(binary.length);
    const view = new Uint8Array(buffer);
    for (let i = 0; i < binary.length; i++) {
      view[i] = binary.charCodeAt(i);
    }
    saveAs(
      new Blob([view], { type: 'application/pdf' }),
      `${sanitizeFilename(programName)}.pdf`,
    );
  } else {
    throw new Error('Error occured while generating pdf.');
  }
};

export const getProgramReportById = (
  programId: string,
): Promise<InputFileValidationApiGatewayResponse> => {
  return new Promise((resolve, reject) => {
    axiosProvider.apiClient
      .get<InputFileValidationApiGatewayResponse>(
        `/program/${encodeURIComponent(
          getProductRouterPath(),
        )}/validation/report?programId=${encodeURIComponent(programId)}`,
      )
      .then((response) => {
        if (response?.data) {
          resolve(response.data);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });
};

export const getOutputLayoutFieldsWithDetailsAndRowColor = async (
  subCode: string,
  version: string,
  fields: {
    outputFields: OutputLayoutField[];
    inputFields: InputLayoutField[];
  },
  delimiter: FieldDelimiter,
): Promise<
  ApiResponse<Record<string, HasRowColor<OutputLayoutFieldWithDetails>[]>>
> => {
  return axiosProvider.apiClient
    .post<
      ApiResponse<Record<string, HasRowColor<OutputLayoutFieldWithDetails>[]>>
    >(
      `/program/${encodeURIComponent(
        getProductRouterPath(),
      )}/output-layout-with-details-and-colors`,
      { ...fields, delimiter, subCode, version },
    )
    .then((response) => {
      const { data, statusText } = response;
      if (!data) {
        throw new Error('Data was not empty/blank.');
      } else if (data.resultCode === 0) {
        return response.data;
      } else {
        throw new Error((data?.messages || []).join('\\n') || statusText);
      }
    });
};

export const getFullAttributesWithDetailsAndRowColor = async (
  subCode: string,
  version: string,
  fullAttributes: FullAttribute[],
): Promise<
  ApiResponse<Record<string, HasRowColor<OutputLayoutFieldWithDetails>[]>>
> => {
  return axiosProvider.apiClient
    .post<
      ApiResponse<Record<string, HasRowColor<OutputLayoutFieldWithDetails>[]>>
    >(
      `/program/${encodeURIComponent(
        getProductRouterPath(),
      )}/full-attributes-with-details-and-colors`,
      {
        fullAttributes: fullAttributes,
        subCode,
        version,
      },
    )
    .then((response) => {
      const { data, statusText } = response;
      if (!data) {
        throw new Error('Data was not empty/blank.');
      } else if (data.resultCode === 0) {
        return response.data;
      } else {
        throw new Error((data?.messages || []).join('\\n') || statusText);
      }
    });
};

export const getScoreDistributionFields = async (
  subCode: string,
  version: string,
  attributes: AttributeLike[],
): Promise<ApiResponse<OutputLayoutFieldWithDetails[]>> => {
  return axiosProvider.apiClient
    .post<ApiResponse<OutputLayoutFieldWithDetails[]>>(
      `/program/${encodeURIComponent(
        getProductRouterPath(),
      )}/score-distribution-report`,
      {
        attributes,
        subCode,
        version,
      },
    )
    .then((response) => {
      const { data, statusText } = response;
      if (!data) {
        throw new Error('Data was not empty/blank.');
      } else if (data.resultCode === 0) {
        return response.data;
      } else {
        throw new Error((data?.messages || []).join('\\n') || statusText);
      }
    });
};

export const getAscendArchivesOutputLayoutFieldsWithDetailsAndRowColor = async (
  subCode: string,
  version: string,
  programId: string,
): Promise<ApiResponse<HasRowColor<OutputLayoutFieldWithDetails>[]>> => {
  return axiosProvider.apiClient
    .post<ApiResponse<HasRowColor<OutputLayoutFieldWithDetails>[]>>(
      `/program/${encodeURIComponent(
        getProductRouterPath(),
      )}/output-layout-with-details-and-colors`,
      {
        programId,
        subCode,
        version,
      },
    )
    .then((response) => {
      const { data, statusText } = response;
      if (!data) {
        throw new Error('Data was not empty/blank.');
      } else if (data.resultCode === 0) {
        return response.data;
      } else {
        throw new Error((data?.messages || []).join('\\n') || statusText);
      }
    });
};

export const getProgramLifeCycleById = (
  programId: string,
): Promise<ProgramLifeCycleApiGatewayResponse> => {
  return new Promise((resolve, reject) => {
    axiosProvider.apiClient
      .get<ProgramLifeCycleApiGatewayResponse>(
        `/program/${encodeURIComponent(
          getProductRouterPath(),
        )}/program-life-cycle/?programId=${encodeURIComponent(programId)}`,
      )
      .then((response) => {
        resolve(response.data);
      })
      .catch((error) => {
        reject(error);
      });
  });
};
